import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';

/**
 * PWA Install Prompt Component
 * 
 * Displays a custom install prompt when the app can be installed
 * Only shows on supported browsers (Chrome, Edge, Samsung Internet, etc.)
 */
const PWAInstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the default browser prompt
      e.preventDefault();
      // Save the event for later use
      setInstallPrompt(e);
      // Show our custom prompt
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowPrompt(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;

    // Track analytics - Install button clicked
    if (window.posthog) {
      window.posthog.capture('pwa_install_clicked', {
        timestamp: new Date().toISOString(),
        page: window.location.pathname
      });
    }

    // Show the browser's install prompt
    installPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await installPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('✅ User accepted the install prompt');
      
      // Track analytics - Install accepted
      if (window.posthog) {
        window.posthog.capture('pwa_install_accepted', {
          timestamp: new Date().toISOString(),
          page: window.location.pathname
        });
      }
    } else {
      console.log('❌ User dismissed the install prompt');
      
      // Track analytics - Install dismissed
      if (window.posthog) {
        window.posthog.capture('pwa_install_dismissed', {
          timestamp: new Date().toISOString(),
          page: window.location.pathname
        });
      }
    }

    // Clear the prompt
    setInstallPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Save dismissal to localStorage to not show again for 7 days
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    
    // Track analytics - Prompt dismissed via X button
    if (window.posthog) {
      window.posthog.capture('pwa_prompt_dismissed', {
        timestamp: new Date().toISOString(),
        page: window.location.pathname,
        method: 'close_button'
      });
    }
  };

  // Don't show if dismissed recently (within 7 days)
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const daysSinceDismissal = (Date.now() - parseInt(dismissed)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissal < 7) {
        setShowPrompt(false);
      }
    }
  }, []);

  if (!showPrompt || !installPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-5">
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 rounded-lg p-2">
              <Download className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Install CGDA SAS App</h3>
              <p className="text-sm text-gray-600">Quick access from your home screen</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-green-600">✓</span>
            <span>Faster loading & better performance</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-green-600">✓</span>
            <span>Works like a native app</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <span className="text-green-600">✓</span>
            <span>Easy access from home screen</span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleDismiss}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Not Now
          </button>
          <button
            onClick={handleInstallClick}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Install App
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
